import { DOCUMENT } from '@angular/common';
import { ElementRef, Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subject, debounceTime, distinctUntilChanged, filter, map, take, timer } from 'rxjs';
import { LoadingService } from '@app/shared/services/loading.service';
import { FramesMainTask } from './frames.definition';
import { findMenu, findUserBlock } from './frames.utils';

export type UserKey =
  | 'USER_ID'
  | 'USER_FULL_NAME'
  | 'USER_ROLE'
  | 'USER_ROLE_DESC'
  | 'ALL_ROLES'
  | 'ALL_ACCOUNT_YEARS'
  | 'CONTRACT_YEAR'
  | 'DEFAULT_YEAR'
  | 'SYSTEM_VER'
  | 'CLIENT_NAME'
  | 'VERSION_NO_20'
  | 'RUN_IN_XCL'
  | 'RUN_ALL_REPORTS_IN_BACKGROUND_ONLY'
  | 'SHOW_RUN_PROGRAM'
  | 'SHOW_CHANGE_PASSWORD'
  | 'CHANGE_PASSWORD_LABEL'
  | 'CLIENT_TYPE'
  | 'SALESFORCE_ID'
  | 'STATE'
  | 'EMAIL'
  | 'USER_NAME_FULL';

@Injectable({
  providedIn: 'root',
})
export class FramesService {
  FRAMES_URL = '/';
  ready$ = new BehaviorSubject<boolean>(false);
  message$ = new Subject<any>();
  request$ = new Subject<any>();
  /**
   * Extract menu configuration from incoming MessageServices
   */
  menu$ = this.message$.pipe(map(findMenu), filter(Boolean));
  get user(): Record<UserKey, any> | null {
    if (!this.Frames || !this.Frames.Application) {
      return null;
    }

    const block = this.Frames.Application.mainTask?.data?.block;

    if (!block) {
      return null;
    }

    const userInfoBlock = block.find((o) => o['@name'] == 'USER_INFO');

    if (userInfoBlock && !this.Frames.isUndef(userInfoBlock.record)) {
      const user: any = {};
      userInfoBlock.record[0].item.forEach((kv) => {
        user[kv['@name']] = kv.value;
      });
      return user as Record<UserKey, any>;
    }

    const userInfo = this.Frames.Application.mainTask?._BLOCKS_?.get?.('USER_INFO');

    if (userInfo) {
      const record: any[] = userInfo._last_model?.record;
      const user: any = {};
      record?.[0]?.item?.forEach((kv: any) => {
        user[kv['@name']] = kv.value;
      });
      return user as Record<UserKey, any>;
    }
    return null;
  }

  user$ = this.message$.pipe(
    map((message) => {
      // grab user from USER_BLOCK
      if (this.user) {
        return this.user;
      }

      // grab user from USER_BLOCK from MessageServices response
      const userBlock = findUserBlock(message);
      if (userBlock) {
        const user: any = {};
        userBlock?.forEach((kv: any) => {
          user[kv['@name']] = kv.value;
        });
        return user as Record<UserKey, any>;
      }

      return null;
    }),
  );

  menuClicked$ = new Subject<void>();

  get Frames() {
    return window.Frames;
  }

  public isFrames$ = new BehaviorSubject(this.router.url === '/');

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly router: Router,
    private loadingService: LoadingService,
  ) {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map((e: any) => e.urlAfterRedirects),
        debounceTime(100),
        distinctUntilChanged(),
        map((url) => url === '/'),
      )
      .subscribe(this.isFrames$);
  }

  get mainTask(): FramesMainTask {
    return window.Frames.Application.getMainTask();
  }

  execute(action: string) {
    this.ready$.pipe(filter(Boolean), take(1)).subscribe(() => {
      window.Frames.Application.execute(action);
    });
  }

  interceptFramesRequests() {
    window.$.ajaxSetup({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      beforeSend: (xhr: any, settings: any) => {
        this.loadingService.trySkipNextRequest(settings.type, settings.url);
        this.loadingService.show();

        const headers = {
          'X-Timezone-Offset': new Date().getTimezoneOffset().toString(),
        };

        Object.entries(headers).forEach(([key, value]) => {
          if (value) {
            xhr.setRequestHeader(key, value);
          }
        });

        if (window.authService?.accessToken) {
          const header = `Bearer ${window.authService.accessToken}`;
          xhr.setRequestHeader('Authorization', header);
        }
      },
      complete: () => {
        this.loadingService.hide();
      },
    });
  }

  /**
   * Setup to intercept menu after Frames has bootstrapped
   */
  private afterBootstrap() {
    this.interceptFramesRequests();
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const framesService = this;

    // Frames available after oninit
    const _serviceCall = window.Frames.Application._service_call;

    window.Frames.Application._service_call = function () {
      _serviceCall();

      const _service = window.Frames.Application._service;

      // Frames.Application._service is available after _service_call
      // _service_call initializes a new service instance for the Frames.Application._service
      // _service (Frames.Service / Frames.ServiceMVC) controls the request/response flow for Frames application
      const successCallback = _service.successCallback;
      const processRequestCallback = _service.processRequest;

      // tap into _service.successCallback to intercept responses
      _service.successCallback = function (response: any) {
        if (response?.header?.[0]?.control?.[0]?.messages?.length ?? 0 > 0) {
          for (const messageList of response.header[0].control[0].messages) {
            if (messageList?.message?.length ?? 0 > 0) {
              for (const message of messageList.message) {
                if (message['@message_title'] === 'FINGERPRINT_MISMATCH') {
                  setTimeout(() => {
                    window.location.replace(window.location.href);
                  }, 3000);
                }
              }
            }
          }
        }
        successCallback(response);
        framesService.message$.next(response);
      };

      _service.processRequest = function (request: any) {
        framesService.request$.next(request);
        return processRequestCallback(request);
      };
    };

    window.Frames.Application.on('viewready', () => {
      if (!this.ready$.value) {
        this.ready$.next(true);
      }
    });
  }

  /**
   * <script src="runtime/js/bootstrap.js"></script>
   * <link href="runtime/css/frames-loader.css" rel="stylesheet" />
   */
  bootstrap(framesNode: ElementRef<any>) {
    // Note: frames-loader.css is now loaded in index.html to prevent FOUC
    const script = this.document.createElement('script');
    script.setAttribute('src', 'runtime/js/bootstrap.js');
    script.onload = () => {
      // Run ERP 2.0 Frames
      window.bs_init({
        oninit: () => {
          this.afterBootstrap();
        },
      });
    };
    framesNode.nativeElement.appendChild(script);
  }

  private getMenuToggle() {
    return document.getElementsByClassName('workspace-menu-toggle')?.[0];
  }

  intercept$ = timer(0, 1000);

  onMenuClick = () => {
    this.menuClicked$.next();
  };

  interceptMenuToggle() {
    // frames will try to inject a click listener onto the menu button
    // need to continually intercept the menu to avoid old menu opening
    this.intercept$.subscribe(() => {
      const menuToggle = this.getMenuToggle();
      if (!menuToggle) {
        return;
      }

      const intercepted = menuToggle.eventListeners('click').includes(this.onMenuClick);

      if (intercepted) {
        return;
      }

      //remove all click listeners by cloning, disables frames menu
      const cloned = menuToggle.cloneNode(true) as Element;

      // add our own click listener to toggle angular menu
      cloned.addEventListener('click', this.onMenuClick);
      menuToggle.replaceWith(cloned);
    });
  }
}
